import { useCallback, useEffect, useMemo } from 'react';

import useRefState from '~/hooks/use-ref-state';
import { isFunction } from '~/components/authentication/utils';
import Loading from '~/components/misc/loading';
import SuspenseWrapper from '~/components/misc/suspense-wrapper';
import useTranslations from '~/components/translation/use-translations';

import WizardProvider from './context';
import {
	DirectionTypes,
	NextCBprops,
	PreviousCBprops,
	StepProps,
	StepTypes,
	StepWizardProps,
	WizardContextType
} from './types';
import { getDefaultStep, getEffectiveSteps, isOfTypeSubStep } from './utils';
import { FLOW_DIRECTION, STEP_TYPES } from './utils/constants';

function StepWizard({
	children,
	complete = () => {},
	exit: exitCB = () => {},
	flow = [],
	init = getDefaultStep,
	next: nextCB,
	previous: previousCB,
	steps = {},
	translationKeys = []
}: StepWizardProps): React.ReactNode {
	const { translations, isLoadingTranslations } = useTranslations(translationKeys);

	const effectiveFlow = useMemo<string[]>(() => getEffectiveSteps({ steps, flow }), [steps, flow]);
	const [activeStep, setActiveStep] = useRefState<string>(init({ steps: effectiveFlow }));
	const [direction, setDirection] = useRefState<DirectionTypes>(FLOW_DIRECTION.forward);
	const [activeSubStep, setActiveSubStep] = useRefState<string | null>(null);
	const [stepType, setStepType] = useRefState<StepTypes>(STEP_TYPES.step);

	const getStepDetails = useCallback((stepName: string): StepProps => steps[stepName], [steps]);

	/**
	 * Get the next step to show based on the direction of the wizard
	 */
	const resolveStep = useCallback(
		(step: string | void, dir = direction.current): string => {
			const currentIndex: number = effectiveFlow.indexOf(step ?? activeStep.current);
			const nextPageIndex = dir === FLOW_DIRECTION.forward ? 1 : -1;

			return effectiveFlow[currentIndex + nextPageIndex];
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[activeStep.current, direction.current, effectiveFlow]
	);

	const exit = useCallback(() => {
		if (isFunction(exitCB)) {
			setDirection(FLOW_DIRECTION.backward);
			exitCB();
		}
	}, [exitCB, setDirection]);

	const next = useCallback(
		({ step, as = STEP_TYPES.step, subStep }: NextCBprops = {}): void => {
			const nextStep = subStep ?? resolveStep(step, FLOW_DIRECTION.forward);

			if (!nextStep) {
				return exit();
			}

			const nextStepDetails = getStepDetails(nextStep);

			if (isOfTypeSubStep(nextStepDetails, as)) {
				setActiveSubStep(nextStepDetails.key);
			} else {
				setActiveStep(nextStepDetails.key);
			}

			setStepType(as);
			setDirection(FLOW_DIRECTION.forward);

			if (nextStepDetails.isValid() && isFunction(nextCB)) {
				return nextCB(nextStepDetails);
			}

			if (nextStepDetails) {
				return next({ step: nextStep });
			}

			exit();
		},
		[exit, getStepDetails, nextCB, resolveStep, setActiveStep, setActiveSubStep, setDirection, setStepType]
	);

	const previous = useCallback(
		({ step }: PreviousCBprops = {}): void => {
			const previousStep = activeSubStep.current
				? activeStep.current
				: resolveStep(step, FLOW_DIRECTION.backward);

			if (!previousStep) {
				return complete();
			}

			const previousStepDetails = getStepDetails(previousStep);

			if (activeSubStep.current) {
				setActiveSubStep(null);
			} else {
				setActiveStep(previousStepDetails.key);
			}

			setDirection(FLOW_DIRECTION.backward);

			if (previousStepDetails.isValid() && isFunction(previousCB)) {
				return previousCB(previousStepDetails);
			}

			if (previousStepDetails) {
				return previous({ step: previousStep });
			}

			complete();
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[
			activeStep.current,
			activeSubStep.current,
			complete,
			getStepDetails,
			previousCB,
			resolveStep,
			setActiveStep,
			setActiveSubStep,
			setDirection
		]
	);

	const reset = useCallback(() => {
		const step = init({ steps: effectiveFlow });

		setActiveStep(step);
		setDirection(FLOW_DIRECTION.backward);
	}, [effectiveFlow, init, setActiveStep, setDirection]);

	// eslint-disable-next-line react-hooks/exhaustive-deps
	const step = useMemo(() => steps[activeStep.current], [activeStep.current, steps]);
	// eslint-disable-next-line react-hooks/exhaustive-deps
	const subStep = useMemo(() => steps[activeSubStep.current as string], [activeSubStep.current, steps]);

	/**
	 * direction --> for storing the direction of the flow
	 * step --> current active step
	 * translations --> preloaded translations of the steps
	 */
	const contextValue = useMemo<WizardContextType>(
		() => ({
			complete,
			direction: direction.current,
			exit,
			getStepDetails,
			next,
			previous,
			reset,
			step,
			stepType: stepType.current,
			subStep,
			translations
		}),
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[direction.current, step, translations, subStep]
	);

	useEffect(() => {
		if (!activeStep.current) {
			exit();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	/**
	 * Render a loader when translations are being loaded
	 * or when activeStep is not resolved
	 */
	if (isLoadingTranslations || !activeStep.current || !step) {
		return <Loading />;
	}

	return (
		<SuspenseWrapper>
			<WizardProvider value={contextValue}>{children}</WizardProvider>
		</SuspenseWrapper>
	);
}

export default StepWizard;
